import { Toaster } from 'react-hot-toast';

import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { isPhoneNumber } from 'class-validator';
import { sendVerificationCodePhone, updateTenantPhone } from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { CANANA_COUNTRY_CODE } from '../../../constants/canadaCountryCode';
import { sanitisePhoneNumber } from '../../utils/phoneNumber';

const UpdatePhone = ({
  phoneNumber,
  onSuccess,
  embedded = false,
}: {
  phoneNumber?: string;
  onSuccess: (phone: string) => void;
  embedded?: boolean;
}) => {
  const { auth } = useAuth();

  const [loading, setLoading] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<string>('');
  const [phone, setPhone] = useState<string>(
    phoneNumber ? sanitisePhoneNumber(phoneNumber).slice(0, 10) : ''
  );

  useEffect(() => {
    setPhone(phoneNumber ? sanitisePhoneNumber(phoneNumber).slice(0, 10) : '');
  }, [phoneNumber]);
  const formatPhoneNumberInput = (value: string) => {
    if (!value) return value;

    const phoneNumber = sanitisePhoneNumber(value);
    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength < 4) return phoneNumber;

    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }

    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };
  const updatePhone = async (phone: string) => {
    const token = auth?.data?.token;
    if (!token) {
      setPhoneError('Authentication token missing. Please log in again.');
      return;
    }

    setLoading(true);
    setPhoneError('');
    try {
      await updateTenantPhone(token, {
        userDetails: { phoneNumber: phone },
      });
      await sendVerificationCodePhone(
        `+${CANANA_COUNTRY_CODE}${phone}`,
        4,
        token
      );
      onSuccess(phone); // trigger parent to swap to VerifyPhoneCode
    } catch (error) {
      const isBlockedPhone =
        error instanceof Error && error.message === 'phone_number_blocked';
      setPhoneError(
        isBlockedPhone
          ? 'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.'
          : 'Failed to update phone number. Please check the number and try again.'
      );
    } finally {
      setLoading(false);
    }
  };
  const content = (
    <div className={embedded ? 'flex flex-col gap-6' : 'max-w-[554px] max-h-[675px] bg-[#FFF] rounded-[12px] flex flex-col overflow-auto px-6'}>
        <div className='flex flex-col gap-10'>
          <div className={embedded ? 'text-[14px] text-[#6B7285]' : 'text-main text-[20px]'}>
            {embedded
              ? 'Enter your phone number. We will send a verification code via SMS.'
              : 'Set a new phone number for your account'}
          </div>
          <div className='flex flex-col gap-[6px]'>
            <div
              className={clsx(
                phoneError && phone !== '' ? 'isInvalid' : '',
                !phoneError && phone ? 'valid' : '',
                'inputField'
              )}
            >
              {phone && <div className='text-[--text-main] text-[16px]/[24px]'>+1</div>}
              <input
                type='text'
                autoComplete='off'
                placeholder='+1 (XXX) XXX-XXXX'
                maxLength={14}
                value={formatPhoneNumberInput(phone)}
                onChange={(e) => {
                  const inputValue = sanitisePhoneNumber(e.target.value).slice(0, 10);
                  setPhone(inputValue);
                  if (inputValue.length === 0) {
                    setPhoneError('Phone number is required');
                  } else if (inputValue.length < 10) {
                    setPhoneError('Phone number must be 10 digits');
                  } else {
                    if (!isPhoneNumber(inputValue, 'CA') && !isPhoneNumber(inputValue, 'US')) {
                      setPhoneError('Please provide a valid Canadian or US phone number');
                    } else setPhoneError('');
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
              />
            </div>
            {phoneError && <p className='error'>{phoneError}</p>}
          </div>
          {!phoneError && (
            <button
              className='submitBtn'
              disabled={loading || !phone || phone.length < 10}
              onClick={() => {
                void updatePhone(phone);
              }}
            >
              {loading ? 'Sending code...' : 'Verify now'}
            </button>
          )}
        </div>
      </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <main className='bg-[#fff] max-w-[404px] rounded-[12px] relative px-[52px] py-[72px]'>
      <Toaster position='top-center' reverseOrder={false} />
      {content}
    </main>
  );
};
export default UpdatePhone;
